import React, { Fragment } from "react";
import { Form } from "react-bootstrap";
import PropTypes from 'prop-types';
import Button from '@material-ui/core/Button';
import { SelectInput, TextInput, RadioInput } from "../../../Helpers/Inputs";
import { InputMaskPhone, InputMaskSSN, InputMaskDate, InputMaskEIN } from "../../../Helpers/Formatters";

export default function BeneficiaryForm(props) {

  const getRelationshipSelectOptions = () => {
    return (
      [
        { value: "Child", label: "Child" },
        { value: "Parent", label: "Parent" },
        { value: "Domestic Partner", label: "Domestic Partner" },
        { value: "Business Partner", label: "Business Partner" },
        { value: "Spouse", label: "Spouse", disabled: props.maritalStatus === "single" },
        { value: "Sibling", label: "Sibling" },
        { value: "Ex-Spouse", label: "Ex-Spouse" },
      ]
    );
  };

  const getPercentageOptions = (i) => {
    let primaryBenePercentTotal = 0;
    let contingentBenePercentTotal = 0;
    let currentType = "";
    const arrayOfInt = [];
    props.beneficiaries.forEach((bene, index) => {
      if (i === index) {
        currentType = bene.beneficiaryType;
      }
      else {
        primaryBenePercentTotal += bene.beneficiaryType === "Primary" ? bene.percentage ? parseInt(bene.percentage, 10) : 0 : 0;
        contingentBenePercentTotal += bene.beneficiaryType === "Contingent" ? bene.percentage ? parseInt(bene.percentage, 10) : 0 : 0;
      }
    });
    const intSize = 100 - (currentType === "Primary" ? primaryBenePercentTotal : contingentBenePercentTotal);
    for (let i = 1; i <= intSize; i++) {
      arrayOfInt.push({ value: `${i}`, label: `${i}` });
    }
    return arrayOfInt;
  };

  const getBeneficiaryOptions = () => {
    return (
      [
        { value: "Individual", label: "Individual" },
        { value: "Estate", label: "Estate" },
        { value: "Trust", label: "Trust" },
      ]
    );
  };

  const getSuffix = () => {
    return [
      { label: "II", value: "II" },
      { label: "III", value: "III" },
      { label: "IV", value: "IV" },
      { label: "Jr.", value: "Jr." },
      { label: "Sr.", value: "Sr." },
      { label: "None", value: "" },
    ];
  };

  const getBeneficiaryTypeSelectOptions = () => {
    return (
      [
        { value: "Primary", label: "Primary" },
        { value: "Contingent", label: "Contingent" },
      ]
    );
  };

  const onFieldChange = (field, value) => {
    // call onChange with the whole updated form
    props.onChange({ ...props.formState, [field]: value });
  };

  // manually call setValue for fields that don't play nice with react-hook-form register
  const handleValueChange = (field, value, longFieldName) => {
    props.setValue(longFieldName, value);
    onFieldChange(field, value);
    props.trigger(longFieldName);
  };

  return (
    <div>
      {props.index !== 0 && (
        <Form.Group>
          <SelectInput
            name={`beneficiaries[${props.index}].beneficiaryType`}
            id={`beneficiaries[${props.index}].beneficiaryType`}
            value={props.formState.beneficiaryType}
            onChange={e => handleValueChange('beneficiaryType', e.target.value, `beneficiaries[${props.index}].beneficiaryType`)}
            label="Select Beneficiary Type"
            selectOptions={getBeneficiaryTypeSelectOptions()}
            errors={props.errors && props.errors.beneficiaryType}
            helpText="A contingent beneficiary is who would receive the death benefit if the primary beneficiary listed is no longer living."
            inputColumnSize={10}
          />
        </Form.Group>
      )}
      <Form.Group>
        <SelectInput
          name={`beneficiaries[${props.index}].beneficiary`}
          id={`beneficiaries[${props.index}].beneficiary`}
          value={props.formState.beneficiary}
          onChange={e => handleValueChange('beneficiary', e.target.value, `beneficiaries[${props.index}].beneficiary`)}
          label="Select Beneficiary"
          selectOptions={getBeneficiaryOptions()}
          errors={props.errors && props.errors.beneficiary}
          inputRef={props.register}
        />
      </Form.Group>

      {props.formState.beneficiary === 'Individual' && (
        <Fragment>
          <Form.Group>
            <TextInput
              name={`beneficiaries[${props.index}].firstName`}
              id={`beneficiaries[${props.index}].firstName`}
              label="First Name"
              value={props.formState.firstName}
              errors={props.errors && props.errors.firstName}
              inputRef={props.register}
              onChange={(e) => onFieldChange('firstName', e.target.value)}
            />
          </Form.Group>

          <Form.Group>
            <TextInput
              name={`beneficiaries[${props.index}].middleInitial`}
              id={`beneficiaries[${props.index}].middleInitial`}
              label="Middle Initial"
              value={props.formState.middleInitial}
              errors={props.errors && props.errors.middleInitial}
              inputRef={props.register}
              onChange={(e) => onFieldChange('middleInitial', e.target.value)}
            />
          </Form.Group>

          <Form.Group>
            <TextInput
              name={`beneficiaries[${props.index}].lastName`}
              id={`beneficiaries[${props.index}].lastName`}
              label="Last Name"
              value={props.formState.lastName}
              errors={props.errors && props.errors.lastName}
              inputRef={props.register}
              onChange={(e) => onFieldChange('lastName', e.target.value)}
            />
          </Form.Group>

          <Form.Group>
            <SelectInput
              name={`beneficiaries[${props.index}].suffix`}
              id={`beneficiaries[${props.index}].suffix`}
              value={props.formState.suffix}
              onChange={e => handleValueChange('suffix', e.target.value, `beneficiaries[${props.index}].suffix`)}
              label="Suffix"
              selectOptions={getSuffix()}
              errors={props.errors && props.errors.suffix}
            />
          </Form.Group>

          <Form.Group>
            <TextInput
              name={`beneficiaries[${props.index}].ssnOrTaxpayerID`}
              id={`beneficiaries[${props.index}].ssnOrTaxpayerID`}
              label="Social Security Number or Taxpayer ID (Optional)"
              value={props.formState.ssnOrTaxpayerID}
              errors={props.errors && props.errors.ssnOrTaxpayerID}
              inputRef={props.register}
              onChange={(e) => handleValueChange('ssnOrTaxpayerID', e.target.value, `beneficiaries[${props.index}].ssnOrTaxpayerID`)}
              InputProps={{
                inputComponent: InputMaskSSN,
              }}
            />
          </Form.Group>

          <Form.Group>
            <SelectInput
              name={`beneficiaries[${props.index}].relationship`}
              id={`beneficiaries[${props.index}].relationship`}
              value={props.formState.relationship}
              onChange={e => handleValueChange('relationship', e.target.value, `beneficiaries[${props.index}].relationship`)}
              label="Relationship"
              selectOptions={getRelationshipSelectOptions()}
              errors={props.errors && props.errors.relationship}
              inputRef={props.register}
            />
          </Form.Group>

          <Form.Group>
            <TextInput
              name={`beneficiaries[${props.index}].dateOfBirth`}
              id={`beneficiaries[${props.index}].dateOfBirth`}
              label="Date of Birth"
              value={props.formState.dateOfBirth}
              errors={props.errors && props.errors.dateOfBirth}
              inputRef={props.register}
              onChange={(e) => handleValueChange('dateOfBirth', e.target.value, `beneficiaries[${props.index}].dateOfBirth`)}
              InputProps={{
                inputComponent: InputMaskDate,
              }}
            />
          </Form.Group>

          <Form.Group>
            <TextInput
              name={`beneficiaries[${props.index}].phoneNumber`}
              id={`beneficiaries[${props.index}].phoneNumber`}
              label="Phone Number"
              value={props.formState.phoneNumber}
              errors={props.errors && props.errors.phoneNumber}
              inputRef={props.register}
              onChange={(e) => handleValueChange('phoneNumber', e.target.value, `beneficiaries[${props.index}].phoneNumber`)}
              InputProps={{
                inputComponent: InputMaskPhone,
              }}
            />
          </Form.Group>

          <Form.Group>
            <TextInput
              name={`beneficiaries[${props.index}].emailAddress`}
              id={`beneficiaries[${props.index}].emailAddress`}
              label="Email"
              value={props.formState.emailAddress}
              errors={props.errors && props.errors.emailAddress}
              inputRef={props.register}
              onChange={(e) => onFieldChange('emailAddress', e.target.value)}
            />
          </Form.Group>
        </Fragment>
      )}

      {props.formState.beneficiary === 'Estate' && (
        <Fragment>
          <input type="hidden"
            name={`beneficiaries[${props.index}].estateName`}
            id={`beneficiaries[${props.index}].estateName`}
            value="Estate of the Insured"
          />
          <Form.Group>
            <TextInput
              name={`beneficiaries[${props.index}].estateRelationship`}
              id={`beneficiaries[${props.index}].estateRelationship`}
              label="Relationship"
              value="Estate of Insured"
              onChange={(e) => { }}
              errors={props.errors && props.errors.estateRelationship}
              inputRef={props.register}
              disabled
            />
          </Form.Group>
        </Fragment>
      )}

      {props.formState.beneficiary === 'Trust' && (
        <Fragment>
          <Form.Group>
            <TextInput
              name={`beneficiaries[${props.index}].trustName`}
              id={`beneficiaries[${props.index}].trustName`}
              label="Trust Name"
              value={props.formState.trustName}
              errors={props.errors && props.errors.trustName}
              inputRef={props.register}
              onChange={(e) => onFieldChange('trustName', e.target.value)}
            />
          </Form.Group>
          <Form.Group>
            <TextInput
              name={`beneficiaries[${props.index}].trustDate`}
              id={`beneficiaries[${props.index}].trustDate`}
              label="Trust Date"
              value={props.formState.trustDate}
              errors={props.errors && props.errors.trustDate}
              inputRef={props.register}
              onChange={(e) => handleValueChange('trustDate', e.target.value, `beneficiaries[${props.index}].trustDate`)}
              InputProps={{
                inputComponent: InputMaskDate,
              }}
            />
          </Form.Group>
          <Form.Group>
            <TextInput
              name={`beneficiaries[${props.index}].trustPhoneNumber`}
              id={`beneficiaries[${props.index}].trustPhoneNumber`}
              label="Phone Number"
              value={props.formState.trustPhoneNumber}
              errors={props.errors && props.errors.trustPhoneNumber}
              inputRef={props.register}
              onChange={(e) => handleValueChange('trustPhoneNumber', e.target.value, `beneficiaries[${props.index}].trustPhoneNumber`)}
              InputProps={{
                inputComponent: InputMaskPhone,
              }}
            />
          </Form.Group>
          <Form.Group>
            <TextInput
              name={`beneficiaries[${props.index}].trustRelationship`}
              id={`beneficiaries[${props.index}].trustRelationship`}
              label="Relationship"
              value="Trust"
              errors={props.errors && props.errors.trustRelationship}
              inputRef={props.register}
              onChange={(e) => { }}
              disabled
            />
          </Form.Group>

          <Form.Group>
            <RadioInput
              name={`beneficiaries[${props.index}].trustIdentifyOption`}
              id={`beneficiaries[${props.index}].trustIdentifyOption`}
              label="Identification Type"
              value={props.formState.trustIdentifyOption}
              radios={[{ value: "SSN", label: "Social Security Number (SSN)" }, { value: "EIN", label: "Employer Identification Number (EIN)" }]}
              onChange={e => onFieldChange('trustIdentifyOption', e.target.value)}
              errors={props.errors && props.errors.trustIdentifyOption}
              inputRef={props.register}
            />
          </Form.Group>

          {props.formState.trustIdentifyOption !== "" &&
            <Form.Group>
              <TextInput
                name={`beneficiaries[${props.index}].trustSsnTaxID`}
                id={`beneficiaries[${props.index}].trustSsnTaxID`}
                label={props.formState.trustIdentifyOption === "SSN" ? "Social Security Number" : "Employer Identification Number"}
                value={props.formState.trustSsnTaxID}
                errors={props.errors && props.errors.trustSsnTaxID}
                inputRef={props.register}
                onChange={(e) => handleValueChange('trustSsnTaxID', e.target.value, `beneficiaries[${props.index}].trustSsnTaxID`)}
                InputProps={{
                  inputComponent: props.formState.trustIdentifyOption === "SSN" ? InputMaskSSN : InputMaskEIN,
                }}
              />
            </Form.Group>
          }

          <Form.Group>
            <TextInput
              name={`beneficiaries[${props.index}].trustEmail`}
              id={`beneficiaries[${props.index}].trustEmail`}
              label="Email"
              value={props.formState.trustEmail}
              errors={props.errors && props.errors.trustEmail}
              inputRef={props.register}
              onChange={(e) => onFieldChange('trustEmail', e.target.value)}
            />
          </Form.Group>
        </Fragment>
      )}
      <Form.Group>
        <SelectInput
          name={`beneficiaries[${props.index}].percentage`}
          id={`beneficiaries[${props.index}].percentage`}
          value={props.formState.percentage}
          onChange={e => handleValueChange('percentage', e.target.value, `beneficiaries[${props.index}].percentage`)}
          label="Percentage of Share"
          selectOptions={getPercentageOptions(props.index)}
          errors={props.errors && props.errors.percentage}
          inputRef={props.register}
        />
      </Form.Group>

      {props.showBeneRemove &&
        <Form.Group>
          <Button onClick={props.onRemove}>Remove this Beneficiary</Button>
        </Form.Group>
      }
    </div>
  );
}

BeneficiaryForm.propTypes = {
  formState: PropTypes.object.isRequired,
  onChange: PropTypes.func.isRequired,
  setValue: PropTypes.func.isRequired,
  index: PropTypes.number.isRequired,
  register: PropTypes.func.isRequired,
  errors: PropTypes.any,
  showBeneRemove: PropTypes.bool,
  onRemove: PropTypes.func.isRequired,
  trigger: PropTypes.func.isRequired,
  maritalStatus: PropTypes.string.isRequired,
  beneficiaries: PropTypes.object.isRequired,
};



// WEBPACK FOOTER //
// ./src/components/Quoters/AmericoLoanProtection/steps/BeneficiaryForm.js